import { AppointmentStatus } from '@shared/types';

/**
 * Reasons the End Session confirmation modal can appear. The literal values
 * are also used as `@reason:<value>` tags in Datadog — keep them stable.
 */
export const END_SESSION_REASONS = {
  STATUS_CHANGE: 'status-change',
  DURATION_MISMATCH: 'duration-mismatch',
} as const;

export type EndSessionReason =
  (typeof END_SESSION_REASONS)[keyof typeof END_SESSION_REASONS];

export interface EndSessionReasonInput {
  selectedStatus: AppointmentStatus | null;
  currentStatus: AppointmentStatus | null | undefined;
  calculatedDuration: number;
}

/**
 * Single source of truth shared by `EndCallModalV2.handleEndSession` (decides
 * whether to open the confirmation modal) and `useEndAppointment.confirmEndCall`
 * (tags the Datadog log). Returns `null` to proceed directly without a modal.
 */
export function computeEndSessionReason({
  selectedStatus,
  currentStatus,
  calculatedDuration,
}: EndSessionReasonInput): EndSessionReason | null {
  if (selectedStatus === null) {
    return null;
  }

  if (currentStatus != null) {
    return selectedStatus !== currentStatus
      ? END_SESSION_REASONS.STATUS_CHANGE
      : null;
  }

  if (
    selectedStatus !== AppointmentStatus.Occurred &&
    calculatedDuration > 0
  ) {
    return END_SESSION_REASONS.DURATION_MISMATCH;
  }

  return null;
}
